import React, { useEffect, useRef, useState, useCallback } from 'react'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'
import { fmtCoins } from '../../lib/cards'
import { io, Socket } from 'socket.io-client'
import { Sounds } from '../../lib/sounds'

// ─── Symbol map (matches slot.js IDs 0-8) ─────────────────────────────────────
const SYM  = ['wild','scatter','seven','grapes','strawberry','cherry','orange','lemon','bell']
const SPATH = (id:number) => `/slot/symbols/${SYM[id]}.png`

// ─── Constants ────────────────────────────────────────────────────────────────
const ROWS     = 3
const NUM_REELS = 5
const GAP      = 4      // px between reels
const PRE_SPIN = 22     // random rows before final result

// ─── Types ────────────────────────────────────────────────────────────────────
interface Props {
  token:string; balance:number|null
  playerId?:string; operatorId?:string; displayName?:string
}
type Phase = 'idle'|'spinning'|'free_spins'|'bonus'|'bonus_reveal'

interface WinLine {
  line:number; symbol:number; count:number; payout:number
  positions:Array<[number,number]>
}
interface BonusOption { id:number; mult:number; stake_win:number }

// ─── Measure hook ─────────────────────────────────────────────────────────────
function useWidth(ref:React.RefObject<HTMLElement>) {
  const [w,setW] = useState(320)
  useEffect(()=>{
    if(!ref.current) return
    const ro = new ResizeObserver(e=>setW(e[0].contentRect.width))
    ro.observe(ref.current)
    setW(ref.current.clientWidth||320)
    return ()=>ro.disconnect()
  },[ref])
  return w
}

// ─── Build reel strip ─────────────────────────────────────────────────────────
function buildStrip(finalCol:[number,number,number]): number[] {
  const pre = Array.from({length:PRE_SPIN},()=>Math.floor(Math.random()*9))
  const buf = Array.from({length:4},()=>Math.floor(Math.random()*9))
  return [...pre,...finalCol,...buf]
}

// ─── Easing ───────────────────────────────────────────────────────────────────
const easeOutQuart=(t:number)=>1-(--t)*t*t*t

// ─── Reel ─────────────────────────────────────────────────────────────────────
interface ReelProps {
  strip:number[]; spinning:boolean; targetY:number
  winRows:Set<number>; cellH:number; cellW:number; reelIdx:number
}
function Reel({strip,spinning,targetY,winRows,cellH,cellW,reelIdx}:ReelProps){
  const drumRef = useRef<HTMLDivElement>(null)
  const rafRef  = useRef(0)
  const curYRef = useRef(0)

  useEffect(()=>{
    cancelAnimationFrame(rafRef.current)
    if(!drumRef.current) return
    if(spinning){
      drumRef.current.style.transition='none'
      drumRef.current.style.transform='translateY(0)'
      curYRef.current=0
    } else {
      const from=curYRef.current, to=targetY
      const dur=850+reelIdx*170
      const t0=performance.now()
      const tick=(now:number)=>{
        const p=Math.min((now-t0)/dur,1)
        const y=from+(to-from)*easeOutQuart(p)
        if(drumRef.current) drumRef.current.style.transform=`translateY(${y}px)`
        curYRef.current=y
        if(p<1) rafRef.current=requestAnimationFrame(tick)
      }
      rafRef.current=requestAnimationFrame(tick)
    }
    return()=>cancelAnimationFrame(rafRef.current)
  },[spinning,targetY,reelIdx])

  return(
    <div style={{
      width:cellW, height:ROWS*cellH+(ROWS-1)*2,
      overflow:'hidden', borderRadius:5, position:'relative',
      background:'rgba(0,0,0,.45)',
      boxShadow:'inset 0 0 10px rgba(0,0,0,.7)',
      flexShrink:0,
    }}>
      <div ref={drumRef} style={{position:'absolute',top:0,left:0,width:'100%'}}>
        {strip.map((sym,i)=>{
          const isResult = i>=PRE_SPIN && i<PRE_SPIN+ROWS
          const rowIdx   = i-PRE_SPIN
          const isWin    = isResult && winRows.has(rowIdx)
          return(
            <div key={i} style={{
              width:cellW, height:cellH,
              display:'flex', alignItems:'center', justifyContent:'center',
              marginBottom:2,
              background:isWin?'rgba(255,215,0,.18)':'transparent',
              outline:isWin?'2px solid rgba(255,215,0,.8)':'none',
              borderRadius:4,
            }}>
              <img
                src={SPATH(sym)} alt={SYM[sym]}
                style={{
                  width:Math.round(cellW*0.82),
                  height:Math.round(cellH*0.82),
                  objectFit:'contain',
                  mixBlendMode:'screen',
                }}
              />
            </div>
          )
        })}
      </div>
      {/* Edge shadows */}
      <div style={{position:'absolute',top:0,left:0,right:0,height:20,
        background:'linear-gradient(to bottom,rgba(0,0,0,.8),transparent)',
        pointerEvents:'none',zIndex:2}}/>
      <div style={{position:'absolute',bottom:0,left:0,right:0,height:20,
        background:'linear-gradient(to top,rgba(0,0,0,.8),transparent)',
        pointerEvents:'none',zIndex:2}}/>
    </div>
  )
}

// ─── SlotGame ─────────────────────────────────────────────────────────────────
export function SlotGame({token,balance:initBalance,playerId:initPid,operatorId:initOid}:Props){
  const wrapRef   = useRef<HTMLDivElement>(null)
  const socketRef = useRef<Socket|null>(null)
  const sessionRef= useRef({playerId:initPid||'',operatorId:initOid||''})
  const timers    = useRef<any[]>([])

  const containerW = useWidth(wrapRef)
  // ── Responsive cell size ──────────────────────────────────────────────────
  const CELL_W = Math.min(110, Math.max(54, Math.floor((containerW - 20 - (NUM_REELS-1)*GAP) / NUM_REELS)))
  const CELL_H = Math.round(CELL_W * 0.95)
  const targetY  = -(PRE_SPIN*(CELL_H+2))

  const [grid,setGrid]      = useState<number[][]>([[8,7,6],[6,5,8],[7,4,5],[5,8,7],[8,6,4]])
  const [strips,setStrips]  = useState<number[][]>(
    Array(NUM_REELS).fill(null).map((_,r)=>buildStrip([grid[r][0],grid[r][1],grid[r][2]] as any))
  )
  const [spinning,setSpinning] = useState<boolean[]>(Array(NUM_REELS).fill(false))
  const [wins,setWins]       = useState<WinLine[]>([])
  const [phase,setPhase]     = useState<Phase>('idle')
  const [balance,setBalance] = useState<number|null>(initBalance)
  const [totalWin,setTotalWin]= useState(0)
  const [stake,setStake]     = useState(20)
  const [freeSpinsLeft,setFreeSpinsLeft] = useState(0)
  const [freeSpinsMult,setFreeSpinsMult] = useState(2)
  const [bonusOptions,setBonusOptions]   = useState<BonusOption[]>([])
  const [pickedBonus,setPickedBonus]     = useState<number|null>(null)
  const [toast,setToast]     = useState('')
  const [winFlash,setWinFlash]= useState(false)
  const [currency,setCurrency]= useState('coins')

  useEffect(()=>{ if(initBalance!==null) setBalance(initBalance) },[initBalance])
  const showToast=(msg:string,dur=2500)=>{setToast(msg);setTimeout(()=>setToast(''),dur)}

  // Win cells per reel
  const reelWinRows=(r:number)=>{
    const s=new Set<number>()
    wins.forEach(w=>w.positions.forEach(([ri,row])=>{ if(ri===r) s.add(row) }))
    return s
  }

  // ─── Start spin animation ────────────────────────────────────────────────
  const triggerSpin=useCallback((resultGrid:number[][])=>{
    const newStrips=resultGrid.map(col=>buildStrip([col[0],col[1],col[2]] as any))
    setStrips(newStrips)
    setSpinning(Array(NUM_REELS).fill(true))
    setWins([]); setTotalWin(0)
    timers.current.forEach(clearTimeout)
    timers.current=[]
    ;[1400,1750,2100,2450,2800].forEach((delay,i)=>{
      const t=setTimeout(()=>{
        setSpinning(prev=>{ const n=[...prev]; n[i]=false; return n })
        Sounds.hop()
      },delay)
      timers.current.push(t)
    })
    const t=setTimeout(()=>setSpinning(Array(NUM_REELS).fill(false)),3200)
    timers.current.push(t)
  },[])

  // ─── Socket ──────────────────────────────────────────────────────────────
  useEffect(()=>{
    const GS=(import.meta as any).env?.VITE_GAME_SERVER_URL||'http://localhost:3002'
    const s=io(GS,{transports:['polling'],auth:{token}})
    socketRef.current=s
    try{
      const p=JSON.parse(atob(token.split('.')[1]))
      sessionRef.current={playerId:p.sub||'',operatorId:p.operator_id||''}
      if(p.currency) setCurrency(p.currency)
    }catch{}

    s.on('connect',async()=>{
      try{
        const {playerId}=sessionRef.current
        const r=await fetch(`${GS}/wallet/balance?player_id=${playerId}`)
        const d=await r.json()
        if(typeof d.balance==='number') setBalance(d.balance)
      }catch{}
    })

    const handleResult=(d:any,isFree=false)=>{
      const g:number[][]=d.grid
      setGrid(g); setWins(d.wins||[])
      if(isFree) setFreeSpinsLeft(d.spins_left??0)
      triggerSpin(g)
      setTimeout(()=>{
        const tw=d.total_win||0; setTotalWin(tw)
        // Update balance AFTER reels stop — so player sees credit only when win is shown
        if(typeof d.balance==='number') setBalance(d.balance)
        if(tw>0){ setWinFlash(true); setTimeout(()=>setWinFlash(false),2000); Sounds.cashout() }
        if(d.free_spins_triggered){ setFreeSpinsLeft(d.free_spins_total||10); setFreeSpinsMult(2); showToast('🌟 10 FREE SPINS!',3000) }
        if(d.retrigger) showToast('🔄 +5 spins!',2000)
        if(d.bonus_triggered&&!d.free_spins_triggered) setPhase('bonus')
        else if(d.free_spins_complete||(!isFree&&!d.free_spins_triggered)) setPhase('idle')
        else if(isFree) setPhase('free_spins')
        else setPhase(d.free_spins_triggered?'free_spins':'idle')
        if(tw>0) showToast(`🏆 WIN ₹${tw.toFixed(2)}!`,2500)
      },3400)
    }

    s.on('slot:result',      d=>handleResult(d,false))
    s.on('slot:free_spin_result', d=>handleResult(d,true))

    s.on('slot:bonus_start',(d:any)=>{
      setBonusOptions(d.options||[]); setPickedBonus(null); setPhase('bonus')
    })
    s.on('slot:bonus_result',(d:any)=>{
      setPickedBonus(d.picked_id); setBonusOptions(d.all_boxes||[])
      if(typeof d.balance==='number') setBalance(d.balance)
      setPhase('bonus_reveal')
      showToast(`🎁 +₹${d.payout.toFixed(2)}!`,3000); Sounds.cashout()
      setTimeout(()=>{ setBonusOptions([]); setPhase('idle') },4000)
    })
    s.on('slot:error',(d:any)=>{ showToast(`❌ ${d.error}`); setPhase('idle') })
    s.on('balance',(d:any)=>{ if(typeof d.balance==='number') setBalance(d.balance) })
    return ()=>{ s.disconnect() }
  },[token,triggerSpin])

  // ─── Actions ─────────────────────────────────────────────────────────────
  const isSpinning=spinning.some(s=>s)||phase==='spinning'
  const canSpin=!isSpinning&&(phase==='idle'||phase==='free_spins')

  const handleSpin=()=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||!canSpin) return
    if(balance!==null&&balance<stake) return showToast('Not enough balance')
    setBalance(b=>b!==null?Math.max(0,b-stake):b)
    setPhase('spinning'); setWins([]); setTotalWin(0)
    Sounds.go()
    if(phase==='free_spins') s.emit('slot:free_spin',{player_id:playerId,operator_id:operatorId})
    else                     s.emit('slot:spin',{player_id:playerId,operator_id:operatorId,stake})
  }

  const handleBonusPick=(id:number)=>{
    if(pickedBonus!==null||phase!=='bonus') return
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s) return
    s.emit('slot:bonus_pick',{player_id:playerId,operator_id:operatorId,pick_id:id})
    Sounds.hop()
  }

  const STAKES=[5,10,20,50,100,200,500]

  return(
    <div style={{
      display:'flex',flexDirection:'column',height:'100%',
      background:'linear-gradient(160deg,#080818 0%,#0d1622 50%,#080818 100%)',
      color:'#fff',fontFamily:'system-ui,sans-serif',userSelect:'none',
      overflow:'hidden', position:'relative',
    }}>
      <style>{`
        @keyframes winPulse{0%,100%{opacity:1}50%{opacity:.3}}
        @keyframes slotToast{0%{opacity:0;transform:translateX(-50%) scale(.85)}15%,85%{opacity:1;transform:translateX(-50%) scale(1)}100%{opacity:0}}
        @keyframes goldGlow{0%,100%{text-shadow:0 0 8px #ffd700}50%{text-shadow:0 0 24px #ffd700,0 0 48px #ff8c00}}
        @keyframes bounce{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
        .sbtn:hover{transform:scale(1.05)}.sbtn:active{transform:scale(.96)}
        .bbox:hover{transform:scale(1.1)!important;border-color:rgba(255,215,0,.9)!important}
      `}</style>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'6px 12px',background:'rgba(0,0,0,.55)',
        borderBottom:'1px solid rgba(255,215,0,.12)',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:16}}>🎰</span>
          <strong style={{fontSize:12,letterSpacing:.5,color:'#ffd700'}}>FRUIT SLOTS</strong>
          {phase==='free_spins'&&(
            <span style={{background:'rgba(255,140,0,.25)',border:'1px solid rgba(255,140,0,.5)',
              borderRadius:10,padding:'1px 7px',fontSize:10,fontWeight:700,color:'#ffa500',
              animation:'bounce .9s infinite'}}>
              FREE ×{freeSpinsLeft}
            </span>
          )}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <SoundToggleBtn/>
          <div style={{background:'rgba(201,168,76,.1)',border:'1px solid rgba(201,168,76,.3)',
            borderRadius:7,padding:'3px 9px',fontWeight:700,color:'#c9a84c',fontSize:12}}>
            {balance!==null?`₹${fmtCoins(balance)}`:'—'}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast&&(
        <div style={{position:'absolute',top:52,left:'50%',zIndex:60,
          animation:'slotToast 2.5s ease-in-out forwards',
          background:'rgba(0,0,0,.92)',color:'#ffd700',padding:'7px 18px',
          borderRadius:20,fontSize:14,fontWeight:800,whiteSpace:'nowrap',
          border:'1px solid rgba(255,215,0,.35)'}}>
          {toast}
        </div>
      )}

      {/* Win flash */}
      {winFlash&&(
        <div style={{position:'absolute',inset:0,zIndex:40,pointerEvents:'none',
          background:'radial-gradient(ellipse at center,rgba(255,215,0,.12) 0%,transparent 70%)',
          animation:'winPulse .4s 4'}}/>
      )}

      {/* ── Reel area ────────────────────────────────────────────────────────── */}
      <div style={{flex:1,display:'flex',flexDirection:'column',
        alignItems:'center',justifyContent:'center',padding:'8px',overflow:'hidden'}}>

        {/* Cabinet */}
        <div style={{
          background:'linear-gradient(180deg,#12122a 0%,#0a0a18 100%)',
          border:'2px solid rgba(255,215,0,.25)',borderRadius:12,
          padding:'10px 10px 7px',width:'100%',maxWidth:620,alignSelf:'center',
          boxShadow:'0 0 30px rgba(255,165,0,.08),inset 0 1px 0 rgba(255,255,255,.04)',
          position:'relative',
        }}>
          {/* Win display */}
          <div style={{textAlign:'center',marginBottom:7,height:26}}>
            {totalWin>0?(
              <span style={{fontSize:20,fontWeight:900,color:'#ffd700',
                animation:'goldGlow 1.5s infinite'}}>
                ₹{totalWin.toFixed(2)}
              </span>
            ):(
              <span style={{fontSize:11,color:'rgba(255,255,255,.2)'}}>
                {isSpinning?'🎰 Spinning...':'Good Luck! 🍀'}
              </span>
            )}
          </div>

          {/* Reels — responsive width */}
          <div ref={wrapRef} style={{display:'flex',gap:GAP,width:'100%',justifyContent:'center'}}>
            {Array(NUM_REELS).fill(null).map((_,r)=>(
              <Reel key={r}
                strip={strips[r]} spinning={spinning[r]} targetY={targetY}
                winRows={reelWinRows(r)} cellH={CELL_H} cellW={CELL_W} reelIdx={r}
              />
            ))}
          </div>

          {/* Win line badges */}
          {wins.length>0&&(
            <div style={{marginTop:6,display:'flex',flexWrap:'wrap',
              justifyContent:'center',gap:3}}>
              {wins.slice(0,8).map((w,i)=>(
                <div key={i} style={{
                  background:'rgba(255,215,0,.12)',border:'1px solid rgba(255,215,0,.35)',
                  borderRadius:10,padding:'1px 6px',fontSize:9,fontWeight:700,color:'#ffd700'}}>
                  L{w.line} ₹{w.payout.toFixed(1)}
                </div>
              ))}
              {wins.length>8&&(
                <div style={{color:'rgba(255,215,0,.4)',fontSize:9,alignSelf:'center'}}>
                  +{wins.length-8}
                </div>
              )}
            </div>
          )}

          {/* Bonus overlay */}
          {(phase==='bonus'||phase==='bonus_reveal')&&(
            <div style={{
              position:'absolute',inset:0,zIndex:30,borderRadius:10,
              background:'rgba(0,0,0,.9)',backdropFilter:'blur(4px)',
              display:'flex',flexDirection:'column',alignItems:'center',
              justifyContent:'center',gap:12,padding:16,
            }}>
              <div style={{fontSize:20,fontWeight:900,color:'#ffd700',textAlign:'center'}}>
                🎁 BONUS ROUND!
              </div>
              <div style={{fontSize:11,color:'rgba(255,255,255,.5)',textAlign:'center'}}>
                {phase==='bonus'?'Pick a box!':'All revealed!'}
              </div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center'}}>
                {bonusOptions.map(opt=>{
                  const revealed=phase==='bonus_reveal'||pickedBonus!==null
                  const isPicked=opt.id===pickedBonus
                  return(
                    <div key={opt.id} className="bbox"
                      onClick={()=>handleBonusPick(opt.id)}
                      style={{
                        width:64,height:64,borderRadius:10,
                        cursor:pickedBonus===null?'pointer':'default',
                        border:`2px solid ${isPicked?'#ffd700':'rgba(255,215,0,.25)'}`,
                        background:isPicked
                          ?'linear-gradient(135deg,#ffd700,#ff8c00)'
                          :revealed?'rgba(255,255,255,.06)':'rgba(255,215,0,.12)',
                        display:'flex',flexDirection:'column',alignItems:'center',
                        justifyContent:'center',transition:'all .18s',
                        fontSize:10,fontWeight:700,
                        color:isPicked?'#000':'#ffd700',
                      }}>
                      {revealed?(
                        <><span style={{fontSize:16}}>🎁</span>{opt.mult}×<br/>₹{opt.stake_win.toFixed(0)}</>
                      ):(
                        <span style={{fontSize:26}}>📦</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Controls ──────────────────────────────────────────────────────────── */}
      <div style={{
        background:'linear-gradient(to top,rgba(0,0,0,.97),rgba(0,0,0,.7))',
        borderTop:'1px solid rgba(255,215,0,.08)',
        padding:'7px 10px 10px',flexShrink:0,
      }}>
        {/* Stake row */}
        <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:7}}>
          <span style={{fontSize:9,color:'rgba(255,255,255,.35)',whiteSpace:'nowrap'}}>BET:</span>
          <div style={{display:'flex',gap:3,overflowX:'auto',paddingBottom:2,flex:1}}>
            {STAKES.map(s=>(
              <button key={s} onClick={()=>!isSpinning&&setStake(s)}
                style={{
                  borderRadius:5,padding:'3px 7px',cursor:isSpinning?'default':'pointer',
                  border:stake===s?'1.5px solid #ffd700':'1.5px solid rgba(255,255,255,.12)',
                  background:stake===s?'rgba(255,215,0,.18)':'rgba(255,255,255,.05)',
                  color:stake===s?'#ffd700':'rgba(255,255,255,.45)',
                  fontSize:10,fontWeight:700,whiteSpace:'nowrap',flexShrink:0,transition:'all .1s',
                }}>
                ₹{s>=1000?`${s/1000}K`:s}
              </button>
            ))}
          </div>
        </div>

        {/* Spin row */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 68px 1fr',gap:8,alignItems:'center'}}>
          <div style={{fontSize:9,color:'rgba(255,255,255,.3)',lineHeight:1.5}}>
            20 lines<br/>
            <span style={{color:'rgba(255,215,0,.45)',fontWeight:700}}>₹{stake} bet</span>
          </div>

          <button className="sbtn" onClick={handleSpin} disabled={!canSpin}
            style={{
              borderRadius:50,width:68,height:68,border:'none',
              cursor:canSpin?'pointer':'default',fontWeight:900,
              fontSize:phase==='free_spins'?9:13,lineHeight:1.2,
              background:
                phase==='free_spins'?'linear-gradient(135deg,#ff8c00,#ff4500)':
                canSpin?'linear-gradient(135deg,#ffd700,#ff8c00)':
                'rgba(255,255,255,.08)',
              color:canSpin?'#000':'rgba(255,255,255,.2)',
              transition:'all .15s',
              boxShadow:canSpin?'0 0 18px rgba(255,215,0,.35)':'none',
            }}>
            {isSpinning?'⏳':phase==='free_spins'?`FREE\n×${freeSpinsMult}`:'SPIN'}
          </button>

          <div style={{textAlign:'right',fontSize:9,color:'rgba(255,255,255,.3)',lineHeight:1.5}}>
            {phase==='free_spins'?(
              <span style={{color:'#ffa500',fontWeight:700}}>
                {freeSpinsLeft} spins left<br/>{freeSpinsMult}× mult
              </span>
            ):totalWin>0?(
              <span style={{color:'#ffd700',fontWeight:700}}>
                Last win<br/>₹{totalWin.toFixed(2)}
              </span>
            ):(
              <span>RTP ~95%<br/>20 paylines</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
